import React, { useEffect, useRef, useState } from 'react';
import { IonSpinner } from '@ionic/react';
import Player from '@vimeo/player';
import type { AdditionalContent } from '@/types/additionalContent';
import { additionalContentService } from '@/services/additionalContentService';
import { badgeModalService } from '@/services/badgeModalService';
import { useProfileStore } from '@/store/useProfileStore';
import { VIDEO_COMPLETION_THRESHOLD_PERCENT } from '@/constants/lessonProgress';

interface VideoStepProps {
  content: AdditionalContent;
}

const isValidVimeoUrl = (url: string): boolean => {
  return /vimeo\.com\/\d+/.test(url) || /player\.vimeo\.com\/video\/\d+/.test(url);
};

export const VideoStep: React.FC<VideoStepProps> = ({ content }) => {
  const playerRef = useRef<Player | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasCompletedRef = useRef(false);
  const profile = useProfileStore((state) => state.activeProfile);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!content.videoUrl || !containerRef.current) {
      setIsLoading(false);
      return;
    }

    if (!isValidVimeoUrl(content.videoUrl)) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    hasCompletedRef.current = false;
    setIsLoading(true);

    const player = new Player(containerRef.current, {
      url: content.videoUrl,
      width: containerRef.current.offsetWidth || 400,
      responsive: true,
      autoplay: false,
      controls: true,
    });
    playerRef.current = player;

    const completeContent = async () => {
      if (hasCompletedRef.current) return;
      hasCompletedRef.current = true;
      try {
        const response = await additionalContentService.completeAdditionalContent(content.id, 100, profile?.id);
        if (response.badgesEarned?.length) {
          response.badgesEarned.forEach((badge) => {
            badgeModalService.show(badge, profile?.id);
          });
        }
      } catch {
        // Silent failure
      }
    };

    player.on('ended', completeContent);
    player.on('timeupdate', (data) => {
      if (Math.round(data.percent * 100) >= VIDEO_COMPLETION_THRESHOLD_PERCENT) {
        completeContent();
      }
    });

    player
      .ready()
      .then(() => {
        if (isMounted) setIsLoading(false);
      })
      .catch(() => {
        if (isMounted) setIsLoading(false);
      });
    player.on('error', () => {
      if (isMounted) setIsLoading(false);
    });

    return () => {
      isMounted = false;
      try {
        playerRef.current?.destroy();
      } catch {
        // Ignore cleanup errors
      }
      playerRef.current = null;
    };
  }, [content.id, content.videoUrl, profile?.id]);

  return (
    <div className="relative w-full min-h-[200px] bg-black rounded overflow-hidden">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-10">
          <IonSpinner name="crescent" color="light" />
        </div>
      )}
      <div ref={containerRef} className="w-full h-full min-h-[200px]" />
    </div>
  );
};
